// src/components/Jokbo/JokboTable.jsx
import {
    Table, TableHead, TableBody, TableRow, TableCell,
    Chip, Stack, Typography, Box, useMediaQuery, Button,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { Link as RouterLink } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';

/* Chip 색상 */
const traitColorMap = {
    도미넌트: 'error', '마스터/미스트레스': 'error', 오너: 'error', '대디/마미': 'error',
    '브랫 테이머': 'error', 사디스트: 'error', 헌터: 'error', 디그레이더: 'error',
    스팽커: 'error', 리거: 'error',
    서브미시브: 'primary', 슬레이브: 'primary', 펫: 'primary', 리틀: 'primary',
    브랫: 'primary', 마조히스트: 'primary', 프레이: 'primary', 디그레이디: 'primary',
    스팽키: 'primary', 로프버니: 'primary',
};

export default function JokboTable({ userList = [], onToggleFavorite }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    return (
        <Table size="small">
            <TableHead sx={{ display: isMobile ? 'none' : 'table-header-group' }}>
                <TableRow>
                    <TableCell>닉네임</TableCell>
                    <TableCell>나이</TableCell>
                    <TableCell>성별(메인성향)</TableCell>
                    <TableCell>세부 성향</TableCell>
                    <TableCell>구인 상태</TableCell>
                    <TableCell>상세 상태</TableCell>
                    <TableCell>관계 플래그</TableCell>
                    <TableCell>오프 여부</TableCell>
                </TableRow>
            </TableHead>

            <TableBody>
                {userList.map(u => {
                    const recruitDetail = (() => {
                        // 구인중 + 기타(직접작성): recruit_note를 그대로 보여줌 (없으면 '기타')
                        if (u.recruit_main === '구인중') {
                            if (u.recruit_sub === '기타') {
                                return u.recruit_note?.trim() || '기타';
                            }
                            return u.recruit_sub;
                        }

                        // 구인안함 + (플파/디엣/연디): 기존처럼 상세 닉네임을 괄호에 표시
                        if (u.recruit_main === '구인안함' && ['플파', '디엣', '연디'].includes(u.recruit_sub)) {
                            return `${u.recruit_sub}${u.recruit_note?.trim() ? ` (${u.recruit_note})` : ''}`;
                        }

                        // 그 외 기본
                        return u.recruit_sub;
                    })();

                    if (isMobile) {
                        return (
                            <TableRow
                                key={u.username}
                                sx={{ display: 'block', p: 1, borderBottom: 1, borderColor: 'divider' }}
                            >
                                <Box sx={{ p: 1, textAlign: 'left' }}>


                                    {/* [✅ 4. 이 Stack으로 감싸서 버튼을 오른쪽에 배치] */}
                                    <Stack
                                        direction="row"
                                        justifyContent="space-between"
                                        alignItems="center"
                                        sx={{ mb: 0.5 }} // 이름과 세부성향 칩 사이의 간격
                                    >
                                        {/* 👈 4-1. 기존 닉네임/정보를 Box로 묶음 */}
                                        <Box>
                                            <Typography fontWeight={600}>
                                                {u.equipped_emoticon_text ? `${u.equipped_emoticon_text} ` : ''}
                                                {u.nickname || u.username}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {u.age ?? '-'}세 · {u.gender ?? '-'} ({u.preference ?? '-'})
                                            </Typography>
                                        </Box>


                                    </Stack>

                                    <Stack
                                        direction="row"
                                        spacing={0.5}
                                        flexWrap="wrap"
                                        sx={{ mt: 0.5, '& .MuiChip-root': { mb: 0.5 } }}
                                    >
                                        {u.detailed_traits?.length
                                            ? u.detailed_traits.map(t => (
                                                <Chip key={t} label={t} color={traitColorMap[t] || 'default'} size="small" />
                                            ))
                                            : <Typography variant="caption">세부 성향 없음</Typography>}
                                    </Stack>

                                    <Typography variant="caption" sx={{ display: 'block', mt: 0.75 }}>
                                        <strong>구인:</strong> {u.recruit_main?.trim() || '-'}
                                    </Typography>
                                    <Typography variant="caption" sx={{ display: 'block' }}>
                                        <strong>상세:</strong> {recruitDetail?.trim() || '-'}
                                    </Typography>
                                    <Typography variant="caption" sx={{ display: 'block' }}>
                                        <strong>플래그:</strong> {u.recruit_flag?.trim() || '-'}
                                    </Typography>
                                    <Typography variant="caption" sx={{ display: 'block' }}>
                                        <strong>오프:</strong> {u.offline_status?.trim() || '-'}
                                    </Typography>
                                    {/* 👇 미니홈 + 즐겨찾기 버튼을 Stack으로 묶어서 배치 */}
                                    <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} alignItems="center">
                                        {/* 미니홈 버튼 (변경 없음, flexGrow 유지) */}
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            endIcon={<OpenInNewIcon fontSize="small" />}
                                            component={RouterLink}
                                            to={`/u/${u.username}/home`}
                                            sx={{ flexGrow: 1, lineHeight: 1.5 }} // flexGrow 유지, lineHeight 추가
                                        >
                                            미니홈
                                        </Button>
                                        {/* 즐겨찾기 버튼 (Button으로 변경, PC와 동일 스타일 적용) */}
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => onToggleFavorite(u.username, u.is_favorite)}
                                            sx={{
                                                minWidth: '30px', // 최소 너비 고정
                                                width: '30px',    // 너비 고정
                                                height: '30px',   // 높이 고정
                                                p: 0,             // 내부 패딩 제거
                                                lineHeight: 1,
                                                borderColor: u.is_favorite ? 'warning.main' : undefined,
                                            }}
                                        >
                                            {/* 아이콘 크기 PC와 동일하게 */}
                                            {u.is_favorite
                                                ? <StarIcon color="warning" sx={{ fontSize: '1.1rem' }} />
                                                : <StarBorderIcon sx={{ fontSize: '1.1rem' }} />
                                            }
                                        </Button>
                                    </Stack>
                                </Box>
                            </TableRow>
                        );
                    }

                    return (
                        <TableRow key={u.username}>
                            <TableCell>
                                <Typography fontWeight={600}>
                                    {u.equipped_emoticon_text ? `${u.equipped_emoticon_text} ` : ''}
                                    {u.nickname || u.username}
                                </Typography>
                                {u.main_title && (
                                    <Typography variant="caption" color="text.secondary">
                                        {u.main_title}
                                    </Typography>
                                )}
                                {u.sub_title && (
                                    <Typography variant="caption" color="text.secondary">
                                        / {u.sub_title}
                                    </Typography>
                                )}
                            </TableCell>

                            <TableCell>{u.age ?? '-'}</TableCell>
                            <TableCell>{`${u.gender ?? '-'} (${u.preference ?? '-'})`}</TableCell>

                            <TableCell>
                                <Stack direction="row" spacing={0.5} flexWrap="wrap">
                                    {u.detailed_traits?.length
                                        ? u.detailed_traits.map(t => (
                                            <Chip key={t} label={t} color={traitColorMap[t] || 'default'} size="small" />
                                        ))
                                        : '-'}
                                </Stack>
                            </TableCell>

                            <TableCell>{u.recruit_main?.trim() || '-'}</TableCell>
                            <TableCell>{recruitDetail?.trim() || '-'}</TableCell>
                            <TableCell>{u.recruit_flag?.trim() || '-'}</TableCell>
                            <TableCell>{u.offline_status?.trim() || '-'}</TableCell>
                        </TableRow>
                    );
                })}
            </TableBody>
        </Table>
    );
}